'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface SellerProfile {
  id: string;
  user_id: string;
  display_name: string;
  bio: string;
  niche: string;
  tiktok_handle: string;
  followers_count: string;
  avg_gmv: string;
  avg_viewers: string;
  total_streams: number;
  rating: number;
  availability: 'available' | 'busy' | 'unavailable';
  hourly_rate: string;
  location: string;
  languages: string[];
}

const NICHES = ['All', 'Beauty', 'Fashion', 'Health', 'Tech', 'Home', 'Food', 'Fitness', 'Gaming', 'Lifestyle'];
const SORT_OPTIONS = [
  { value: 'rating_desc', label: 'Top Rated' },
  { value: 'avg_gmv_desc', label: 'Highest GMV' },
  { value: 'followers_desc', label: 'Most Followers' },
  { value: 'total_streams_desc', label: 'Most Streams' },
  { value: 'created_at_desc', label: 'Newest' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'all', label: 'All Sellers' },
  { value: 'available', label: 'Available Now' },
  { value: 'busy', label: 'Busy' },
];

const ACCENT_COLORS = ['bg-theme-01', 'bg-theme-02', 'bg-theme-03', 'bg-theme-04'];
const AVATAR_IMGS = [10, 20, 25, 30, 33, 40, 45, 50, 55, 60, 65, 70];

function getAccent(id: string) {
  const idx = id.charCodeAt(0) % ACCENT_COLORS.length;
  return ACCENT_COLORS[idx];
}

function getAvatarImg(id: string) {
  const idx = id.charCodeAt(0) % AVATAR_IMGS.length;
  return AVATAR_IMGS[idx];
}

export default function BrowseSellersPage() {
  const [sellers, setSellers] = useState<SellerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeNiche, setActiveNiche] = useState('All');
  const [availability, setAvailability] = useState('all');
  const [sortBy, setSortBy] = useState('rating_desc');
  const [totalCount, setTotalCount] = useState(0);

  const fetchSellers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const supabase = createClient();
      let query = supabase
        .from('seller_profiles')
        .select('*', { count: 'exact' });

      if (activeNiche !== 'All') {
        query = query.ilike('niche', `%${activeNiche}%`);
      }
      if (availability !== 'all') {
        query = query.eq('availability', availability);
      }
      if (search.trim()) {
        query = query.or(
          `display_name.ilike.%${search.trim()}%,niche.ilike.%${search.trim()}%,location.ilike.%${search.trim()}%`
        );
      }

      switch (sortBy) {
        case 'rating_desc':
          query = query.order('rating', { ascending: false });
          break;
        case 'total_streams_desc':
          query = query.order('total_streams', { ascending: false });
          break;
        case 'created_at_desc':
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('rating', { ascending: false });
      }

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;
      setSellers(data || []);
      setTotalCount(count || 0);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load sellers');
    } finally {
      setLoading(false);
    }
  }, [activeNiche, availability, sortBy, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSellers();
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchSellers, search]);

  const availabilityLabel = (status: string) => {
    if (status === 'available') return { label: 'Available', cls: 'bg-theme-green/15 text-theme-green' };
    if (status === 'busy') return { label: 'Busy', cls: 'bg-theme-04/20 text-dark/60' };
    return { label: 'Unavailable', cls: 'bg-neutral-02 text-dark/40' };
  };

  return (
    <main className="font-montserrat bg-white text-dark overflow-x-hidden min-h-screen">
      <Navbar />

      {/* Page Header */}
      <section className="pt-28 pb-10 bg-neutral-01">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="mb-4">
                <div
                  className="bg-theme-02 text-dark text-[13px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
                  style={{ transform: 'rotate(-4deg)' }}
                >
                  Live Commerce Talent
                </div>
              </div>
              <h1 className="text-[40px] md:text-[52px] font-bold text-dark leading-tight mb-3">
                Browse Sellers
              </h1>
              <p className="text-[17px] text-dark/60 max-w-[520px] leading-relaxed">
                Discover verified TikTok Shop live sellers. Filter by niche, availability, and more to find your perfect match.
              </p>
            </div>
            <div className="flex-shrink-0 bg-white rounded-[20px] px-6 py-4 shadow-sm">
              <p className="text-[13px] text-dark/40 font-medium mb-0.5">Total Sellers</p>
              <p className="text-[36px] font-bold text-dark leading-none">{totalCount}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + Content */}
      <section className="py-10 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">

          {/* Filter Bar */}
          <div className="bg-neutral-01 rounded-[24px] p-5 mb-8 flex flex-col gap-4">
            {/* Search + Sort row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-dark/30" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                  <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, niche, or location…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-white border border-neutral-02 rounded-xl pl-10 pr-4 py-3 text-[14px] text-dark placeholder-dark/30 focus:outline-none focus:border-dark/30 transition-colors"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white border border-neutral-02 rounded-xl px-4 py-3 text-[14px] text-dark font-medium focus:outline-none focus:border-dark/30 transition-colors cursor-pointer min-w-[160px]"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Niche + Availability row */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex flex-wrap gap-2 flex-1">
                {NICHES.map((niche) => (
                  <button
                    key={niche}
                    onClick={() => setActiveNiche(niche)}
                    className={`font-semibold text-[13px] px-4 py-1.5 rounded-full transition-all duration-200 ${
                      activeNiche === niche
                        ? 'bg-dark text-white' :'bg-white text-dark hover:bg-neutral-02 border border-neutral-02'
                    }`}
                  >
                    {niche}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {AVAILABILITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setAvailability(opt.value)}
                    className={`font-semibold text-[13px] px-4 py-1.5 rounded-full transition-all duration-200 ${
                      availability === opt.value
                        ? 'bg-dark text-white' :'bg-white text-dark hover:bg-neutral-02 border border-neutral-02'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Results count */}
          {!loading && (
            <div className="flex items-center justify-between mb-6">
              <p className="text-[14px] text-dark/50 font-medium">
                {sellers.length === 0
                  ? 'No sellers found'
                  : `Showing ${sellers.length} seller${sellers.length !== 1 ? 's' : ''}`}
              </p>
              {(activeNiche !== 'All' || availability !== 'all' || search) && (
                <button
                  onClick={() => { setActiveNiche('All'); setAvailability('all'); setSearch(''); }}
                  className="text-[13px] font-semibold text-dark/50 hover:text-dark transition-colors flex items-center gap-1.5"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-neutral-01 rounded-[24px] p-6 animate-pulse">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-14 h-14 bg-neutral-02 rounded-2xl" />
                    <div className="flex-1">
                      <div className="h-4 bg-neutral-02 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-neutral-02 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {[1,2,3].map(j => <div key={j} className="h-14 bg-neutral-02 rounded-xl" />)}
                  </div>
                  <div className="flex gap-2 mb-5">
                    {[1,2,3].map(j => <div key={j} className="h-6 w-16 bg-neutral-02 rounded-full" />)}
                  </div>
                  <div className="h-10 bg-neutral-02 rounded-xl" />
                </div>
              ))}
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <p className="text-[16px] font-semibold text-dark mb-2">Failed to load sellers</p>
              <p className="text-[14px] text-dark/50 mb-6">{error}</p>
              <button
                onClick={fetchSellers}
                className="font-bold text-[14px] text-white bg-dark px-6 py-3 rounded-pill hover:bg-opacity-80 transition-all"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && sellers.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-neutral-01 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#120A0B" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="9" cy="7" r="4" stroke="#120A0B" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#120A0B" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <p className="text-[18px] font-bold text-dark mb-2">No sellers found</p>
              <p className="text-[14px] text-dark/50 mb-6">Try adjusting your filters or search terms.</p>
              <button
                onClick={() => { setActiveNiche('All'); setAvailability('all'); setSearch(''); }}
                className="font-bold text-[14px] text-dark border border-dark/20 px-6 py-3 rounded-pill hover:bg-neutral-01 transition-all"
              >
                Clear all filters
              </button>
            </div>
          )}

          {/* Seller Cards Grid */}
          {!loading && !error && sellers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {sellers.map((seller) => {
                const avail = availabilityLabel(seller.availability);
                const accent = getAccent(seller.id);
                const avatarImg = getAvatarImg(seller.id);
                return (
                  <div
                    key={seller.id}
                    className="bg-neutral-01 rounded-[24px] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <img
                            src={`https://i.pravatar.cc/56?u=${seller.user_id}`}
                            alt={`${seller.display_name || 'Seller'} profile photo`}
                            className="w-14 h-14 rounded-2xl object-cover"
                          />
                          {seller.availability === 'available' && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-theme-green rounded-full border-2 border-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-dark text-[16px] leading-tight">
                            {seller.display_name || 'Unnamed Seller'}
                          </p>
                          <p className="text-dark/50 text-[13px] mt-0.5">{seller.niche || 'General'}</p>
                        </div>
                      </div>
                      <div className={`w-8 h-8 ${accent} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="#120A0B"/>
                        </svg>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-5">
                      <div className="bg-white rounded-xl p-2.5 text-center">
                        <p className="text-[11px] text-dark/50 mb-0.5">GMV/mo</p>
                        <p className="text-[13px] font-bold text-dark">{seller.avg_gmv || '—'}</p>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 text-center">
                        <p className="text-[11px] text-dark/50 mb-0.5">Followers</p>
                        <p className="text-[13px] font-bold text-dark">{seller.followers_count || '—'}</p>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 text-center">
                        <p className="text-[11px] text-dark/50 mb-0.5">Rating</p>
                        <p className="text-[13px] font-bold text-dark">
                          {seller.rating ? `⭐ ${Number(seller.rating).toFixed(1)}` : '—'}
                        </p>
                      </div>
                    </div>

                    {/* Extra info row */}
                    <div className="flex flex-wrap gap-3 mb-5 text-[12px] text-dark/50">
                      {seller.location && (
                        <span className="flex items-center gap-1">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                          </svg>
                          {seller.location}
                        </span>
                      )}
                      {seller.total_streams > 0 && (
                        <span className="flex items-center gap-1">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          {seller.total_streams} streams
                        </span>
                      )}
                      {seller.hourly_rate && (
                        <span className="flex items-center gap-1">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          {seller.hourly_rate}
                        </span>
                      )}
                    </div>

                    {/* Languages */}
                    {seller.languages && seller.languages.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        {seller.languages.slice(0, 3).map((lang) => (
                          <span key={lang} className="text-[11px] font-medium text-dark/60 bg-white px-3 py-1 rounded-full">
                            {lang}
                          </span>
                        ))}
                        {seller.languages.length > 3 && (
                          <span className="text-[11px] font-medium text-dark/40 bg-white px-3 py-1 rounded-full">
                            +{seller.languages.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-neutral-02">
                      <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${avail.cls}`}>
                        {avail.label}
                      </span>
                      <Link
                        href={`/seller/${seller.id}`}
                        className="font-bold text-[13px] text-dark group-hover:text-theme-01 transition-colors duration-200 flex items-center gap-1"
                      >
                        View Profile
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
